/**
 * Helper functions for store integration patterns in interview request components
 */

/**
 * Generic store data loading helper
 * @param storeData - The data from the store
 * @param fieldMappings - Object mapping store fields to component fields
 * @param componentData - The component's data object to update
 */
export const loadStoredData = (storeData: any, fieldMappings: Record<string, string>, componentData: any): void => {
  Object.entries(fieldMappings).forEach(([storeField, componentField]) => {
    if (storeData[storeField] !== undefined && storeData[storeField] !== null) {
      componentData[componentField] = storeData[storeField];
    }
  });
};

/**
 * Generic store data saving helper
 * @param action - The Vuex action to dispatch
 * @param transformedData - The data to save to the store
 */
export const saveToStore = (action: Function, transformedData: any): void => {
  action(transformedData);
};

/**
 * Generic form state restoration helper
 * @param storeData - The data from the store
 * @param componentData - The component's data object to update
 * @param fieldMappings - Object mapping store fields to component fields
 */
export const restoreFormState = (storeData: any, componentData: any, fieldMappings: Record<string, string>): void => {
  Object.entries(fieldMappings).forEach(([storeField, componentField]) => {
    if (storeData[storeField] !== undefined && storeData[storeField] !== null) {
      componentData[componentField] = storeData[storeField];
    }
  });
};

/**
 * Generic data transformation helper for store saving
 * @param componentData - The component's data
 * @param transformationRules - Object defining how to transform each field
 * @returns Transformed data object ready for store
 */
// eslint-disable-next-line no-unused-vars
export const transformDataForStore = (componentData: any, transformationRules: Record<string, (value: any) => any>): any => {
  const transformedData: any = {};

  Object.entries(transformationRules).forEach(([field, transformer]) => {
    if (componentData[field] !== undefined) {
      transformedData[field] = transformer(componentData[field]);
    }
  });

  return transformedData;
};

/**
 * Validates if store data exists and has meaningful values
 * @param storeData - The data from the store
 * @returns Boolean indicating if store data is valid
 */
export const validateStoreData = (storeData: any): boolean => {
  if (!storeData) return false;
  return !Object.values(storeData).every((val) => val === null || val === undefined);
};

/**
 * Converts boolean virtual value to string representation for form handling
 * @param virtual - Boolean value from store
 * @returns String representation for component
 */
export const convertVirtualToString = (virtual: boolean | null | undefined): string => {
  if (virtual === true) return 'phone';
  if (virtual === false) return 'in_person';
  return '';
};

/**
 * Restores date and time fields from store data
 * @param storeData - The data from the store
 * @param component - The component instance to update
 * @param extractTimeToDate - Function to extract time from datetime
 */
export const restoreDateTimeFromStore = (storeData: any, component: any, extractTimeToDate: Function): void => {
  if (storeData.startTime) {
    const startDateTime = new Date(storeData.startTime);
    component.startDate = startDateTime;
    component.start = extractTimeToDate(startDateTime, component.startDate);
  }

  if (storeData.endTime) {
    const endDateTime = new Date(storeData.endTime);
    component.end = extractTimeToDate(endDateTime, component.startDate);
  }
};
